import React, { ReactElement, useRef, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './Modal.module.scss';
import { Close } from '../../assets/icons/Close';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

export interface IModalWrapper {
  closeModal?: () => void;
  open: boolean;
  children: ReactElement;
  image?: string;
  imageAlt?: string;
  contentPosition?: 'center' | 'top';
}

const Modal = ({
  closeModal,
  open,
  children,
  image,
  imageAlt,
  contentPosition = 'center',
}: IModalWrapper): ReactElement => {
  const nodeRef = useRef(null);

  // removes scroll on body when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = 'visible';
      };
    }
  }, [open]);

  return (
    <div>
      <CSSTransition
        nodeRef={nodeRef}
        in={open}
        timeout={300}
        unmountOnExit
        classNames={{
          enter: styles['modal-enter'],
          enterActive: styles['modal-enter-active'],
          exit: styles['modal-exit'],
          exitActive: styles['modal-exit-active'],
        }}
      >
        <div className={styles['dialog-modal']} ref={nodeRef}>
          <button className={styles['close-button']} aria-label="close" onClick={closeModal}>
            <Close />
          </button>

          <div
            className={cx('container', {
              [`container-${contentPosition}`]: contentPosition,
            })}
          >
            {image && (
              <div className={styles['image-container']}>
                <img className={styles['styled-image']} loading="lazy" src={image} alt={imageAlt} />
              </div>
            )}
            <div
              className={cx('content-wrapper', {
                'content-wrapper-full-width': !image,
                'content-wrapper-with-image': image,
              })}
            >
              <div className={styles['content']}>{children}</div>
            </div>
          </div>
        </div>
      </CSSTransition>
    </div>
  );
};

export default Modal;
